import React from 'react';
import { ProgressBar, OverlayTrigger, Tooltip } from 'react-bootstrap';

const TrafficLight = ({ row, tooltipContainerRef }) => {
    const {
        noStatus = 0,
        stopped = 0,
        notStarted = 0,
        delayedProgress = 0,
        onTrackProgress = 0,
        completed = 0,
    } = row || {};

    const totalCount = Math.round(row?.measures || 0);

    if (totalCount === 0 || (noStatus + stopped + notStarted + delayedProgress + onTrackProgress + completed) === 0) {
        return <div>Keine Einträge</div>;
    }

    const rawTotal = noStatus + stopped + notStarted + delayedProgress + onTrackProgress + completed;
    let adjustedNoStatus = noStatus;
    let adjustedStopped = stopped;
    let adjustedNotStarted = notStarted;
    let adjustedDelayedProgress = delayedProgress;
    let adjustedOnTrackProgress = onTrackProgress;
    let adjustedCompleted = completed;

    if (rawTotal !== 0 && rawTotal !== 100) {
        const max = Math.max(noStatus, stopped, notStarted, delayedProgress, onTrackProgress, completed);
        const diff = 100 - rawTotal;

        if (max === noStatus) adjustedNoStatus += diff;
        else if (max === stopped) adjustedStopped += diff;
        else if (max === notStarted) adjustedNotStarted += diff;
        else if (max === delayedProgress) adjustedDelayedProgress += diff;
        else if (max === onTrackProgress) adjustedOnTrackProgress += diff;
        else adjustedCompleted += diff;
    }

    const noStatusCount = Math.round(noStatus / 100 * totalCount);
    const stoppedCount = Math.round(stopped / 100 * totalCount);
    const notStartedCount = Math.round(notStarted / 100 * totalCount);
    const delayedProgressCount = Math.round(delayedProgress / 100 * totalCount);
    const onTrackProgressCount = Math.round(onTrackProgress / 100 * totalCount);
    const completedCount = Math.round(completed / 100 * totalCount);

    const tooltipIds = {
        noStatus: `tl-tooltip-noStatus-${row?.id || 'default'}`,
        stopped: `tl-tooltip-stopped-${row?.id || 'default'}`,
        notStarted: `tl-tooltip-notStarted-${row?.id || 'default'}`,
        delayedProgress: `tl-tooltip-delayed-${row?.id || 'default'}`,
        onTrackProgress: `tl-tooltip-onTrack-${row?.id || 'default'}`,
        completed: `tl-tooltip-completed-${row?.id || 'default'}`,
        noMeasures: 'tl-tooltip-no-measures',
    };

    if (totalCount === 0 || rawTotal === 0) {
        return (
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip id={tooltipIds.noMeasures}>
                        Keine Einträge
                    </Tooltip>
                }
                container={tooltipContainerRef?.current}
            >
                <ProgressBar style={{ height: '24px', maxWidth: '100%' }}>
                    <ProgressBar variant="secondary" now={100} key="empty-bar-segment" />
                </ProgressBar>
            </OverlayTrigger>
        );
    }

    const noStatusColor = 'var(--status-no-status)';
    const stoppedColor = 'var(--status-stopped)';
    const notStartedColor = 'var(--status-not-started)';
    const delayedColor = 'var(--status-negative)';
    const onTrackColor = 'var(--status-on-track)';
    const completedColor = 'var(--status-positive)';

    return (
        <div className="progress" style={{ height: '24px', maxWidth: '100%', width: '100%' }}>
            {adjustedNoStatus > 0 && (
                <OverlayTrigger
                    key="noStatus-segment-trigger"
                    placement="top"
                    overlay={
                        <Tooltip id={tooltipIds.noStatus}>
                            keine Angabe: {noStatus}% ({noStatusCount})
                        </Tooltip>
                    }
                    container={tooltipContainerRef?.current}
                >
                    <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${adjustedNoStatus}%`, backgroundColor: noStatusColor }}
                        aria-valuenow={adjustedNoStatus}
                        aria-valuemin="0"
                        aria-valuemax="100"
                    />
                </OverlayTrigger>
            )}
            {adjustedStopped > 0 && (
                <OverlayTrigger
                    key="stopped-segment-trigger"
                    placement="top"
                    overlay={
                        <Tooltip id={tooltipIds.stopped}>
                            gestoppt: {stopped}% ({stoppedCount})
                        </Tooltip>
                    }
                    container={tooltipContainerRef?.current}
                >
                    <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${adjustedStopped}%`, backgroundColor: stoppedColor }}
                        aria-valuenow={adjustedStopped}
                        aria-valuemin="0"
                        aria-valuemax="100"
                    />
                </OverlayTrigger>
            )}
            {adjustedNotStarted > 0 && (
                <OverlayTrigger
                    key="notStarted-segment-trigger"
                    placement="top"
                    overlay={
                        <Tooltip id={tooltipIds.notStarted}>
                            nicht begonnen: {notStarted}% ({notStartedCount})
                        </Tooltip>
                    }
                    container={tooltipContainerRef?.current}
                >
                    <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${adjustedNotStarted}%`, backgroundColor: notStartedColor }}
                        aria-valuenow={adjustedNotStarted}
                        aria-valuemin="0"
                        aria-valuemax="100"
                    />
                </OverlayTrigger>
            )}
            {adjustedDelayedProgress > 0 && (
                <OverlayTrigger
                    key="delayedProgress-segment-trigger"
                    placement="top"
                    overlay={
                        <Tooltip id={tooltipIds.delayedProgress}>
                            in Bearbeitung - verzögert: {delayedProgress}% ({delayedProgressCount})
                        </Tooltip>
                    }
                    container={tooltipContainerRef?.current}
                >
                    <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${adjustedDelayedProgress}%`, backgroundColor: delayedColor }}
                        aria-valuenow={adjustedDelayedProgress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                    />
                </OverlayTrigger>
            )}
            {adjustedOnTrackProgress > 0 && (
                <OverlayTrigger
                    key="onTrackProgress-segment-trigger"
                    placement="top"
                    overlay={
                        <Tooltip id={tooltipIds.onTrackProgress}>
                            in Bearbeitung - nach Plan: {onTrackProgress}% ({onTrackProgressCount})
                        </Tooltip>
                    }
                    container={tooltipContainerRef?.current}
                >
                    <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${adjustedOnTrackProgress}%`, backgroundColor: onTrackColor }}
                        aria-valuenow={adjustedOnTrackProgress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                    />
                </OverlayTrigger>
            )}
            {adjustedCompleted > 0 && (
                <OverlayTrigger
                    key="completed-segment-trigger"
                    placement="top"
                    overlay={
                        <Tooltip id={tooltipIds.completed}>
                            abgeschlossen: {completed}% ({completedCount})
                        </Tooltip>
                    }
                    container={tooltipContainerRef?.current}
                >
                    <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${adjustedCompleted}%`, backgroundColor: completedColor }}
                        aria-valuenow={adjustedCompleted}
                        aria-valuemin="0"
                        aria-valuemax="100"
                    />
                </OverlayTrigger>
            )}
        </div>
    );
};

export default TrafficLight;
